// Minimal ZIP writer — STORE only, no compression, no dependency.
//
// The one thing this app needs a zip for is the ten SCB transfer files, which
// are ~20 KB of text between them. Deflate would save nothing worth a library,
// and a store-only archive is a format every unzip on earth reads. If a future
// caller needs real compression, reach for fflate instead of growing this.

const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[i] = c >>> 0
  }
  return t
})()

function crc32(bytes) {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

// DOS date/time, the only timestamp the base ZIP header carries. Seconds have
// 2-second resolution — that is the format, not a rounding bug.
function dosStamp(d) {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1)
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()
  return { time, date }
}

/**
 * @param {{name: string, text: string}[]} entries
 * @returns {Blob} application/zip
 */
export function zipText(entries, now = new Date()) {
  const enc = new TextEncoder()
  const { time, date } = dosStamp(now)
  const parts = []
  const central = []
  let offset = 0

  for (const entry of entries) {
    const name = enc.encode(entry.name)
    const body = enc.encode(entry.text)
    const crc = crc32(body)

    // Local file header (30 bytes + name). Bit 11 of the flags marks the name
    // as UTF-8; our names are ASCII but it costs nothing and keeps Thai safe.
    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true) // signature
    local.setUint16(4, 20, true) // version needed
    local.setUint16(6, 0x0800, true) // flags: UTF-8 name
    local.setUint16(8, 0, true) // method: store
    local.setUint16(10, time, true)
    local.setUint16(12, date, true)
    local.setUint32(14, crc, true)
    local.setUint32(18, body.length, true) // compressed size
    local.setUint32(22, body.length, true) // uncompressed size
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true) // extra field length
    parts.push(new Uint8Array(local.buffer), name, body)

    // Central directory record (46 bytes + name).
    const dir = new DataView(new ArrayBuffer(46))
    dir.setUint32(0, 0x02014b50, true)
    dir.setUint16(4, 20, true) // version made by
    dir.setUint16(6, 20, true) // version needed
    dir.setUint16(8, 0x0800, true)
    dir.setUint16(10, 0, true)
    dir.setUint16(12, time, true)
    dir.setUint16(14, date, true)
    dir.setUint32(16, crc, true)
    dir.setUint32(20, body.length, true)
    dir.setUint32(24, body.length, true)
    dir.setUint16(28, name.length, true)
    dir.setUint16(30, 0, true) // extra
    dir.setUint16(32, 0, true) // comment
    dir.setUint16(34, 0, true) // disk number
    dir.setUint16(36, 0, true) // internal attrs
    dir.setUint32(38, 0, true) // external attrs
    dir.setUint32(42, offset, true) // offset of local header
    central.push(new Uint8Array(dir.buffer), name)

    offset += 30 + name.length + body.length
  }

  const centralSize = central.reduce((n, p) => n + p.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)
  end.setUint16(4, 0, true) // this disk
  end.setUint16(6, 0, true) // disk with central dir
  end.setUint16(8, entries.length, true)
  end.setUint16(10, entries.length, true)
  end.setUint32(12, centralSize, true)
  end.setUint32(16, offset, true)
  end.setUint16(20, 0, true) // comment length

  return new Blob([...parts, ...central, new Uint8Array(end.buffer)], { type: 'application/zip' })
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
